import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLotBids } from '../../hooks/useLotBids';
import { calculateNextMinBid } from '../../services/bidding.service';
import { submitBid, describeOutcome } from '../../services/bidOutcome';
import { useBidOutcomeNotice } from './useBidOutcomeNotice';
import { BidOutcomeNotice } from './BidOutcomeNotice';
import { isRegisteredForSale } from '../../services/registration.service';
import { Auction, BidIncrementBand } from '../../types/auction.types';
import { ProxyBidModal } from './ProxyBidModal';
import { BidHistoryList } from './BidHistoryList';
import toast from 'react-hot-toast';

interface BidPanelProps {
  auctionId: string;
  lotId: string;
  lotNumber: string;
  lotTitle: string;
  startingBid: number; // In cents
  auctionStatus: Auction['status'];
  bidIncrements?: BidIncrementBand[]; // Per-event schedule; falls back to DEFAULT_SCHEDULE
  /** registrationClosedText(auction): when non-empty, shown INSTEAD of the Register button. */
  registrationClosedText?: string;
}

export const BidPanel: React.FC<BidPanelProps> = ({
  auctionId,
  lotId,
  lotNumber,
  lotTitle,
  startingBid,
  auctionStatus,
  bidIncrements,
  registrationClosedText = '',
}) => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const { bidState, loading } = useLotBids(auctionId, lotId, currentUser?.uid, bidIncrements);

  // Per-sale registration awareness (mirrors SalePage). A signed-in user who hasn't registered
  // for THIS sale must be routed into the registration flow — not shown bid controls that the
  // server rejects with "Please register for this sale before bidding."
  const lotPath = `/auctions/${auctionId}/lots/${lotId}`;
  const [registered, setRegistered] = useState(false);
  const [regChecked, setRegChecked] = useState(false);
  useEffect(() => {
    let active = true;
    (async () => {
      if (!currentUser) { if (active) { setRegistered(false); setRegChecked(true); } return; }
      try {
        const r = await isRegisteredForSale(auctionId, currentUser.uid);
        if (active) { setRegistered(r); setRegChecked(true); }
      } catch {
        if (active) { setRegistered(false); setRegChecked(true); }
      }
    })();
    return () => { active = false; };
  }, [currentUser, auctionId]);
  
  const [customBidAmount, setCustomBidAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [showProxyModal, setShowProxyModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // The last attempt's result, shown inline until dismissed or the next attempt; an unconfirmed
  // result updates itself when the server decides (useBidOutcomeNotice).
  const { notice: outcome, show: showOutcome, clear: clearOutcome } = useBidOutcomeNotice(auctionId, lotId);

  // SETTLING STATE: after the user places their OWN bid, the server-side proxy engine may
  // auto-settle ~1s later. Showing "You're Winning!" in that window then flipping to "Outbid"
  // reads as deceptive. So after the user's own placeBid/placeProxyBid resolves, hold a
  // neutral "Confirming bid…" indicator for the FULL SETTLE_MS window, unconditionally.
  //
  // We deliberately do NOT early-clear on "a newer bid arrived": a newer bid EXISTING is not
  // the same as the outbid-marking having landed. On a tie/max, the sniper's bid and the
  // proxy's response are BOTH isWinning until the proxy bid's own trigger runs Phase 1, so an
  // early-clear would release the badge mid-window and flash "You're Winning!". Holding the
  // fixed window is simple, predictable, and leaks nothing (no proxyBids inspection).
  const SETTLE_MS = 2500;
  const [settling, setSettling] = useState(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enterSettling = () => {
    setSettling(true);
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => setSettling(false), SETTLE_MS);
  };

  // Clear immediately (used when a placement FAILS, so a rejected bid doesn't leave the user
  // stuck on "Confirming bid…").
  const cancelSettling = () => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    setSettling(false);
  };

  // Clean up the timer on unmount.
  useEffect(() => () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
  }, []);

  // Determine if this lot has any bids yet
  const hasNoBids = !bidState.currentBid || bidState.bidCount === 0;
  
  // Current bid is either the actual current bid or the starting bid
  const currentBid = bidState.currentBid || startingBid;
  
  // For first bid, minimum is starting bid
  // For subsequent bids, minimum is current bid + increment
  const nextMinBid = hasNoBids
    ? startingBid
    : (bidState.nextMinBid || calculateNextMinBid(currentBid, bidIncrements));

  console.log('🎯 BidPanel state:', {
    hasNoBids,
    startingBid,
    currentBid,
    nextMinBid,
    bidCount: bidState.bidCount,
  });

  const handleQuickBid = async (amount: number) => {
    if (!currentUser || !userProfile) {
      toast.error('Please sign in to bid');
      return;
    }

    if (auctionStatus !== 'live') {
      toast.error('Auction is not live');
      return;
    }

    setPlacing(true);
    // Gate the badge from the FIRST frame — BEFORE awaiting. Otherwise the live snapshot
    // delivers the user's own isWinning bid and paints a green "You're Winning!" for a frame
    // before settling turns on. On failure we cancel it below so the user isn't stuck.
    enterSettling();

    clearOutcome();
    try {
      // ATTEMPT — the server decides. No local check of time, minimum or clock refuses this.
      // Placing… stays on (buttons disabled, no second write) until the outcome is known.
      const o = await submitBid({
        auctionId, lotId, amountCents: amount, bidderId: currentUser.uid,
        bidderName: userProfile.displayName || 'Anonymous', bidderNumber: userProfile.bidderNumber,
      });
      if (o.kind === 'accepted') {
        toast.success(describeOutcome(o, amount, lotNumber).text);
        setCustomBidAmount('');
        // Restart the settle window from the moment of the GRANT, so a proxy response that
        // lands right after it doesn't flash "You're Winning!" then "Outbid".
        enterSettling();
      } else {
        showOutcome(o, amount, lotNumber);
        cancelSettling();
      }
    } catch (error) {
      console.error('Bid placement error:', error);
      showOutcome({ kind: 'error', detail: (error as any)?.message }, amount, lotNumber);
      cancelSettling();
    } finally {
      setPlacing(false);
    }
  };

  const handleCustomBid = () => {
    const amount = parseFloat(customBidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid bid amount');
      return;
    }

    const amountInCents = Math.round(amount * 100);
    handleQuickBid(amountInCents);
  };

  if (loading) {
    return (
      <div className="card">
        <p className="text-center text-gray-600">Loading bid information...</p>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Place Your Bid</h2>

        {/* Current Bid Display */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">
                {hasNoBids ? 'Starting Bid' : 'Current Bid'}
              </p>
              <p className="text-4xl font-bold text-primary-600">
                ${(currentBid / 100).toFixed(2)}
              </p>
              {bidState.bidCount > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {bidState.bidCount} {bidState.bidCount === 1 ? 'bid' : 'bids'}
                </p>
              )}
            </div>
            
            {placing ? (
              <div className="text-right">
                <span className="inline-block px-4 py-2 bg-gray-400 text-white rounded-lg font-bold">
                  ⏳ Placing…
                </span>
              </div>
            ) : settling ? (
              <div className="text-right">
                <span className="inline-block px-4 py-2 bg-gray-400 text-white rounded-lg font-bold">
                  ⏳ Confirming bid…
                </span>
              </div>
            ) : bidState.amIWinning ? (
              <div className="text-right">
                <span className="inline-block px-4 py-2 bg-green-500 text-white rounded-lg font-bold">
                  🏆 You're Winning!
                </span>
              </div>
            ) : bidState.myHighestBid ? (
              <div className="text-right">
                <span className="inline-block px-4 py-2 bg-red-500 text-white rounded-lg font-bold">
                  ⚠️ Outbid
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  Your bid: ${(bidState.myHighestBid.amount / 100).toFixed(2)}
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Result of the last attempt — accepted / refused (with the server's reason) / could not confirm. */}
        <BidOutcomeNotice outcome={outcome} onDismiss={clearOutcome} className="mb-4" />

        {/* Bidding Controls */}
        {auctionStatus === 'live' ? (
          <>
            {/* Not Signed In → sign in, returning to THIS lot afterward */}
            {!currentUser ? (
              <div className="text-center py-6 space-y-4">
                <p className="text-gray-600">Please sign in to place bids</p>
                <Link to={`/login?next=${encodeURIComponent(lotPath)}`} className="btn-primary inline-block">
                  Sign In to Bid
                </Link>
              </div>
            ) : !regChecked ? (
              <div className="text-center py-6 text-gray-600">Checking registration…</div>
            ) : !registered && registrationClosedText ? (
              /* Registration closed / Bid Off: no Register button, no path that leads nowhere. */
              <div className="text-center py-6">
                <p className="text-gray-600">{registrationClosedText}</p>
              </div>
            ) : !registered ? (
              /* Signed in but NOT registered for this sale → route into the SAME registration
                 flow SalePage uses, returning to this lot afterward. Bidding without registering
                 is server-rejected, so we must not show bid controls here. */
              <div className="text-center py-6 space-y-4">
                <p className="text-gray-600">Register for this sale to place bids.</p>
                <button
                  type="button"
                  onClick={() => navigate(`/sale/${auctionId}?returnTo=${encodeURIComponent(lotPath)}`)}
                  className="btn-primary inline-block"
                >
                  Register to Bid
                </button>
              </div>
            ) : (
              <>
                {/* One-tap bid at the next minimum. Executes immediately (same placeBid
                    path). The +$1/+$2/+$5 jump buttons were removed — a button labeled with a
                    higher amount that silently jumps the bid reads as deceptive; typing an
                    amount in Custom Bid below is the deliberate jump-the-bid path. */}
                <div className="mb-4">
                  <button
                    onClick={() => handleQuickBid(nextMinBid)}
                    disabled={placing || bidState.amIWinning}
                    className="btn-primary w-full py-3 text-lg font-bold"
                  >
                    Bid ${(nextMinBid / 100).toFixed(2)}
                    <span className="text-xs block mt-1 opacity-80 font-normal">
                      {hasNoBids ? 'Starting Bid' : 'Next Minimum Bid'}
                    </span>
                  </button>
                </div>

                {/* Custom Bid Amount */}
                <div className="border-t pt-4 mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Jump Bid To:</p>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min={(nextMinBid / 100).toFixed(2)}
                        value={customBidAmount}
                        onChange={(e) => setCustomBidAmount(e.target.value)}
                        placeholder={(nextMinBid / 100).toFixed(2)}
                        className="input pl-7"
                        disabled={placing || bidState.amIWinning}
                      />
                    </div>
                    <button
                      onClick={handleCustomBid}
                      disabled={placing || !customBidAmount || bidState.amIWinning}
                      className="btn-primary px-8"
                    >
                      Bid
                    </button>
                  </div>
                </div>

                {/* Proxy Bid Button */}
                <div className="border-t pt-4">
                  <button
                    onClick={() => setShowProxyModal(true)}
                    className="w-full btn-secondary"
                    disabled={placing}
                  >
                    🤖 Set Proxy Bid (Auto-Bid)
                  </button>
                  <p className="text-xs text-gray-600 mt-2 text-center">
                    Set your max bid and we'll automatically bid for you
                  </p>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-600">
              {auctionStatus === 'scheduled' && 'Auction has not started yet'}
              {auctionStatus === 'draft' && 'Auction is in draft mode'}
              {(auctionStatus === 'ended' || auctionStatus === 'confirmed' || (auctionStatus as string) === 'completed') && 'Bids No Longer Being Accepted'}
            </p>
          </div>
        )}

        {/* Bid History Toggle */}
        {bidState.bidCount > 0 && (
          <div className="border-t mt-4 pt-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-primary-600 hover:text-primary-700 text-sm font-semibold"
            >
              {showHistory ? '▼' : '▶'} View Bid History ({bidState.bidCount})
            </button>
            {showHistory && (
              <div className="mt-4">
                <BidHistoryList bids={bidState.bids} currentUserId={currentUser?.uid} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Proxy Bid Modal */}
      {showProxyModal && (
        <ProxyBidModal
          auctionId={auctionId}
          lotId={lotId}
          lotNumber={lotNumber}
          lotTitle={lotTitle}
          currentBid={currentBid}
          nextMinBid={nextMinBid}
          onClose={() => setShowProxyModal(false)}
          onPlacing={() => enterSettling()}
          onPlaceFailed={() => cancelSettling()}
        />
      )}
    </>
  );
};